import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as ep from '../api/endpoints';
import { useAuth } from '../app/auth';
import { ErrorBox, Field } from '../components/ui';

function Brand() {
  return (
    <>
      <img src="/logo.jpg" alt="aksanenerji logosu" />
      <h1 className="center">aksanenerji</h1>
      <p className="center muted" style={{ marginTop: -8 }}>Fiş takip</p>
    </>
  );
}

/** Giriş: Gmail adresi + şifre. Herkese açık kayıt yok; kullanıcıları yönetici tanımlar (PDF §04). */
export function LoginScreen() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(username, password);
      nav('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <form className="login-box stack" onSubmit={submit}>
        <Brand />
        <ErrorBox error={error} />
        <Field label="E-posta (Gmail)"><input type="text" inputMode="email" autoComplete="username" autoCapitalize="none" placeholder="ad.soyad@gmail.com" value={username} onChange={(e) => setUsername(e.target.value)} required /></Field>
        <Field label="Şifre"><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
        <button className="btn primary block" disabled={busy}>{busy ? 'Giriş yapılıyor…' : 'Giriş yap'}</button>
        <Link to="/ilk-giris" className="btn block" style={{ textDecoration: 'none' }}>İlk giriş: kodla şifremi belirle</Link>
        <p className="center small muted">Hesabınızı yönetici tanımlar ve size 6 haneli bir kod verir. Şifrenizi unuttuysanız yöneticiniz sıfırlar; yeni kodla buradan şifrenizi yeniden belirlersiniz.</p>
        <p className="center small"><Link to="/kur">Uygulamayı cihazıma kur</Link></p>
      </form>
    </div>
  );
}

/** İlk giriş veya yönetici sıfırlaması sonrası: e-posta + yöneticinin verdiği kod + yeni şifre. */
export function FirstLoginScreen() {
  const { firstLogin } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: '', code: '', password: '', password2: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (form.password !== form.password2) { setError(new Error('Şifreler eşleşmiyor.')); return; }
    setBusy(true);
    setError(null);
    try {
      await firstLogin(form.email.trim(), form.code, form.password);
      nav('/', { replace: true });
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="login-wrap">
      <form className="login-box stack" onSubmit={submit}>
        <Brand />
        <p className="center muted">Yöneticinizin verdiği kodla kendi şifrenizi belirleyin.</p>
        <ErrorBox error={error} />
        <Field label="E-posta (Gmail)"><input type="email" autoComplete="username" autoCapitalize="none" placeholder="ad.soyad@gmail.com" value={form.email} onChange={set('email')} required /></Field>
        <Field label="6 haneli kod"><input inputMode="numeric" autoComplete="one-time-code" placeholder="482913" value={form.code} onChange={set('code')} required pattern="[0-9 \-]{6,9}" /></Field>
        <Field label="Yeni şifre (en az 10 karakter)"><input type="password" value={form.password} onChange={set('password')} required minLength={10} autoComplete="new-password" /></Field>
        <Field label="Yeni şifre tekrar"><input type="password" value={form.password2} onChange={set('password2')} required minLength={10} autoComplete="new-password" /></Field>
        <button className="btn primary block" disabled={busy}>{busy ? 'Kaydediliyor…' : 'Şifremi belirle ve giriş yap'}</button>
        <p className="center small muted">Kod tek kullanımlıktır ve 72 saat geçerlidir. 5 hatalı denemeden sonra yöneticinizden yeni kod isteyin.</p>
        <p className="center small"><Link to="/giris">Girişe dön</Link></p>
      </form>
    </div>
  );
}

export function AcceptInviteScreen() {
  const { token = '' } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ username: '', full_name: '', password: '', password2: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (form.password !== form.password2) { setError(new Error('Şifreler eşleşmiyor.')); return; }
    setBusy(true);
    setError(null);
    try {
      await ep.acceptInvite({ token, username: form.username, full_name: form.full_name, password: form.password });
      nav('/giris', { replace: true, state: { welcome: true } });
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="login-wrap">
      <form className="login-box stack" onSubmit={submit}>
        <Brand />
        <p className="center muted">Davetinizi kabul edip hesabınızı oluşturun.</p>
        <ErrorBox error={error} />
        <Field label="Ad soyad"><input value={form.full_name} onChange={set('full_name')} required maxLength={120} /></Field>
        <Field label="Kullanıcı adı"><input value={form.username} onChange={set('username')} required pattern="[a-zA-Z0-9_.\-]{3,64}" autoComplete="username" /></Field>
        <Field label="Şifre (en az 10 karakter)"><input type="password" value={form.password} onChange={set('password')} required minLength={10} autoComplete="new-password" /></Field>
        <Field label="Şifre tekrar"><input type="password" value={form.password2} onChange={set('password2')} required minLength={10} autoComplete="new-password" /></Field>
        <button className="btn primary block" disabled={busy}>Hesabı oluştur</button>
      </form>
    </div>
  );
}

export function PasswordResetScreen() {
  const { token } = useParams();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [pw, setPw] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (token) {
        await ep.confirmPasswordReset(token, pw);
        nav('/giris', { replace: true });
      } else {
        setDone((await ep.requestPasswordReset(identifier)).message);
      }
    } catch (err) { setError(err); }
  }

  return (
    <div className="login-wrap">
      <form className="login-box stack" onSubmit={submit}>
        <Brand />
        <ErrorBox error={error} />
        {done ? <div className="alert ok">{done}</div> : token ? (
          <Field label="Yeni şifre (en az 10 karakter)"><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={10} required autoComplete="new-password" /></Field>
        ) : (
          <Field label="Kullanıcı adı veya e-posta"><input value={identifier} onChange={(e) => setIdentifier(e.target.value)} required /></Field>
        )}
        {!done && <button className="btn primary block">{token ? 'Şifreyi kaydet' : 'Sıfırlama bağlantısı gönder'}</button>}
        <p className="center small"><Link to="/giris">Girişe dön</Link></p>
      </form>
    </div>
  );
}
